import React, { useEffect, useState, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { auth } from "./firebase";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { db } from "./firebase";
import { doc, getDoc, collection, query, where, getDocs, writeBatch } from "firebase/firestore";
import GanniGamesHome from "./GanniGamesHome";
import AccountPage from "./AccountPage";
import WCPool from "./WCPool";
import UEApp from "./UEApp";
import FPApp from "./FPApp";
import SignIn from "./SignIn";
import DisplayName from "./DisplayName";
import "./App.css";

// ─── Shared Auth Context ──────────────────────────────────────────────────────
export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// ─── Loading Spinner ──────────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", flexDirection: "column", gap: 16 }}>
      <svg width={60} height={60} viewBox="0 0 100 100" className="animate-spin-slow" style={{ flexShrink: 0 }}>
        <circle cx="50" cy="50" r="48" fill="white" stroke="#ddd" strokeWidth="1"/>
        <polygon points="50,10 62,30 38,30" fill="#111"/>
        <polygon points="90,35 72,28 68,46" fill="#111"/>
        <polygon points="82,72 68,60 76,44" fill="#111"/>
        <polygon points="50,88 60,70 40,70" fill="#111"/>
        <polygon points="18,72 32,60 24,44" fill="#111"/>
        <polygon points="10,35 28,28 32,46" fill="#111"/>
        <polygon points="50,30 64,46 58,64 42,64 36,46" fill="#111"/>
      </svg>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, color: "#FFD700", letterSpacing: 2 }}>LOADING...</div>
    </div>
  );
}

// ─── Auth Gate ────────────────────────────────────────────────────────────────
// Wraps game routes — shows sign-in / display name screens if not authenticated
export function AuthGate({ children }) {
  const { user, displayName, authLoading, setDisplayName } = useAuth();

  // Save the full intended URL before auth so we can restore it after sign-in
  useEffect(() => {
    if (!user && !authLoading) {
      try {
        const params = new URLSearchParams(window.location.search);
        const joinCode = params.get("join");
        if (joinCode && window.location.pathname.includes("underdog-edge")) {
          sessionStorage.setItem("ue_pendingJoinCode", joinCode.toUpperCase());
          // Also save the full path so we land in the right place
          sessionStorage.setItem("ue_pendingPath", window.location.pathname + window.location.search);
        }
        if (joinCode && window.location.pathname.includes("franchise-player")) {
          sessionStorage.setItem("fp_pendingJoinCode", joinCode.toUpperCase());
        }
      } catch (e) {}
    }
  }, [user, authLoading]);

  if (authLoading) return <LoadingScreen />;
  if (!user) return <div style={{ maxWidth: 480, margin: "40px auto", padding: "0 16px" }}><SignIn /></div>;
  if (!displayName) return (
    <DisplayName user={user} onComplete={(name) => {
      setDisplayName(name);
      // After display name set, check if there's a pending UE join in the current URL or sessionStorage
      try {
        const params = new URLSearchParams(window.location.search);
        const joinCode = params.get("join") ||
          sessionStorage.getItem("ue_pendingJoinCode");
        if (joinCode && window.location.pathname.includes("underdog-edge")) {
          sessionStorage.removeItem("ue_pendingJoinCode");
          sessionStorage.removeItem("ue_pendingPath");
          // Small delay to let setDisplayName propagate before redirect
          setTimeout(() => {
            window.location.replace(`/underdog-edge?join=${joinCode.toUpperCase()}`);
          }, 100);
        }
        const fpJoinCode = params.get("join") || sessionStorage.getItem("fp_pendingJoinCode");
        if (fpJoinCode && window.location.pathname.includes("franchise-player")) {
          sessionStorage.removeItem("fp_pendingJoinCode");
          setTimeout(() => {
            window.location.replace(`/franchise-player?join=${fpJoinCode.toUpperCase()}`);
          }, 100);
        }
      } catch (e) {}
    }} />
  );

  return children;
}

// ─── Root Provider ────────────────────────────────────────────────────────────
function AppContent() {
  const [user, setUser]               = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    // Check who's signed in straight away. (There used to be a getRedirectResult()
    // call first, but every sign-in here uses a pop-up or email/password, so it
    // never found anything. On phones its hidden handshake could stall for up to
    // 30 seconds and hold up every page.)
    const authUnsubscribe = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) await loadDisplayName(u.uid);
      else setDisplayName(null);
      setAuthLoading(false);
    });
    return () => authUnsubscribe();
  }, []);

  const loadDisplayName = async (uid) => {
    try {
      const snap = await getDoc(doc(db, "users", uid));
      if (snap.exists()) {
        const name = snap.data().displayName;
        setDisplayName(name);
        if (name) syncDisplayNameToEntries(uid, name);
      } else {
        setDisplayName(null);
      }
    } catch { setDisplayName(null); }
  };

  // Keep WC Pool entry display names in sync (preserving existing behavior)
  const syncDisplayNameToEntries = async (uid, name) => {
    try {
      const snap = await getDocs(query(collection(db, "entries"), where("userId", "==", uid)));
      const stale = snap.docs.filter(d => d.data().userName !== name);
      if (!stale.length) return;
      const batch = writeBatch(db);
      stale.forEach(d => batch.update(d.ref, { userName: name }));
      await batch.commit();
    } catch (e) { console.error("Name sync failed:", e); }
  };

  const logout = () => {
    signOut(auth);
    setDisplayName(null);
    setUser(null);
  };

  // After auth + display name resolved, check for a pending UE join code
  // This handles: new users after display name setup, AND already-logged-in users following invite links
  useEffect(() => {
    if (!user || !displayName) return;
    try {
      const pendingCode = sessionStorage.getItem("ue_pendingJoinCode");
      const pendingPath = sessionStorage.getItem("ue_pendingPath");
      if (pendingCode) {
        sessionStorage.removeItem("ue_pendingJoinCode");
        sessionStorage.removeItem("ue_pendingPath");
        const destination = pendingPath || `/underdog-edge?join=${pendingCode}`;
        window.location.replace(destination);
      }
    } catch (e) {}
  }, [user, displayName]);

  // Also check on initial load for already-logged-in users who followed an invite link
  // The AuthGate won't fire for them since they're already authed, so we check here
  useEffect(() => {
    if (!user || !displayName || authLoading) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const joinCode = params.get("join");
      // If already at /underdog-edge with a join code, do nothing — UEContestHome handles it
      // If somehow at wrong route with a pending code in session, redirect
      const pendingCode = sessionStorage.getItem("ue_pendingJoinCode");
      if (pendingCode && !window.location.pathname.includes("underdog-edge")) {
        sessionStorage.removeItem("ue_pendingJoinCode");
        sessionStorage.removeItem("ue_pendingPath");
        window.location.replace(`/underdog-edge?join=${pendingCode}`);
      }
      // Same fallback for Franchise Player invite links
      const fpPendingCode = sessionStorage.getItem("fp_pendingJoinCode");
      if (fpPendingCode && !window.location.pathname.includes("franchise-player")) {
        sessionStorage.removeItem("fp_pendingJoinCode");
        window.location.replace(`/franchise-player?join=${fpPendingCode}`);
      }
    } catch (e) {}
  }, [user, displayName, authLoading]);

  const effectiveUser = user ? { ...user, displayName: displayName || user.displayName } : null;

  return (
    <AuthContext.Provider value={{ user: effectiveUser, displayName, setDisplayName, authLoading, logout }}>
      <Routes>
        <Route path="/" element={<GanniGamesHome />} />
        <Route path="/wc-pool/*" element={
          <AuthGate><WCPool /></AuthGate>
        } />
        <Route path="/underdog-edge/*" element={
          <AuthGate><UEApp /></AuthGate>
        } />
        <Route path="/franchise-player/*" element={
          <AuthGate><FPApp /></AuthGate>
        } />
        <Route path="/account" element={<AuthGate><AccountPage /></AuthGate>} />
        {/* Catch-all → home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthContext.Provider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}
